import {
  Button,
  Checkbox,
  CloseIcon,
  Dialog,
  Flex,
  ProviderConsumer
} from "@fluentui/react-northstar";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { appState, useSetState } from "../AppState";
import { LoadingScreen } from "../shared/components/LoadingScreen";
import { platformService } from "../shared/services/platform.service";
import { GETTING_ALL_TEMPLATES, STATUS_GUID, toastDefault } from "../shared/utils/constants";
import { canParseJson, parseJsonRecovering } from "../shared/utils/jsonRecovery";
import { TemplateFormStateProps } from "./TemplateFieldsForm";
import { TicketTemplatesSelection } from "./TicketTemplatesSelection";
import { TicketTemplatesSelectionComplete } from "./TicketTemplatesSelectionComplete";
import { TicketTemplatesSelectionView } from "./TicketTemplatesSelectionView";

import { useBoolean } from "@fluentui/react-hooks";
import { toast } from "react-toastify";
import MgtCss from '../mgt.module.css';
import { startFileUploadSession } from "../shared/components/EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent";
import { formatInputRawData } from "../shared/components/TicketTemplateAnswer";
import { uploadService } from "../shared/services/upload.service";
import { getThemeMode } from "../shared/utils/helper";
import { EditTicketEvent } from "../tikit/ticketHelper";
import { SEND_FORM_OPTION } from "./CustomForms/constants";
import { defaultCustomFormButtonStateValue } from "./CustomForms/CustomForm.Interface";
import { getDefaultCustomFormList, handleBackCustomFormAction, handleNextCustomFormAction, handleSendCustomForm, handleStartOVerCustomFormAction, setDefaultCustomFormState, updateCustomFormState, updateIsCustomFormSavingState, updateIsSummaryPageState, validateRequiredInputs } from "./CustomForms/CustomForm.Service";

const api = new platformService();
const uploadHelper = new uploadService();

export interface TemplateListDialogProp {
  isNewTicket: boolean;
  formState: any;
  showTemplate: boolean;
  isApplyingTemplateWithLifecycle: boolean;
  templateList: any[];
  templateListLoaded: boolean;
  dataSourceList: any[];
  dispatch: React.Dispatch<any>;
  closeApplyTemplate: VoidFunction;
  applyTemplate: VoidFunction;
  ticketApprovals: TicketApprovals[];
  sendFormTemplate?: boolean;
  onInteraction?: any;
}

export const sendRequestToApplyLifecycle = async function (
  selectedLifecycleId: number,
  ticketId: number
) {
  try {
    let result = await api.applyLifecycle(ticketId, selectedLifecycleId);
    return parseInt(result.data.data);
  } catch (e) {
    console.error(e);
    return 0;
  }
};

export const TemplateListDialog: React.FC<TemplateListDialogProp> = (props) => {
  let {
    isNewTicket,
    formState,
    showTemplate,
    isApplyingTemplateWithLifecycle,
    templateList,
    templateListLoaded,
    dataSourceList,
    dispatch,
    closeApplyTemplate,
    applyTemplate,
    ticketApprovals
  } = props;

  const { t } = useTranslation();

  const currentState = appState();
  const setContextState = useSetState();

  const [templatePage, setTemplatePage] = useState(0);
  const [showBackToTemplateButton, setShowBackToTemplateButton] = useState(false);
  const [showSendCurrentAnswerToggle, setShowSendCurrentAnswerToggle] = useState(true);
  const [sendCurrentAnswer, { toggle: toggleSendCurrentAnswer, setTrue: setTrueSendCurrentAnswer, setFalse: setFalseSendCurrentAnswer }] = useBoolean(false);
  const [disableSendCurrentAnswerToggle, setDisableSendCurrentAnswerToggle] = useState(true);
  const [customFormButtonState, setCustomFormButtonState] = useState(defaultCustomFormButtonStateValue);
  const [continueButtonText, setContinueButtonText] = useState(t('template.template-form-submit'));
  const [showSummaryCard, setShowSummaryCard] = useState(false);
  const [disableSaveSendBtn, setDisableSaveSendBtn] = useState(false);

  const templatePages = ["select", "done"];
  useEffect(() => {

    if (showTemplate && currentState.openSendFormDialogFrom != "updateAnswer") {
      setTemplatePage(0);
      setCustomFormButtonState(defaultCustomFormButtonStateValue);
    }

    switch (currentState.openSendFormDialogFrom) {
      case 'sendForm':
        return setTemplatePage(1);
      case 'updateAnswer':
        return setTemplatePage(1);
      case 'applyTemplate':
      default:
        return setTemplatePage(0);
    }
  }, [showTemplate]);

  useEffect(() => {
    if (formState.SelectedTemplate.TemplateForms && formState.SelectedTemplate.TemplateForms.length > 0) {
      setDefaultCustomFormState(formState.SelectedTemplate.TemplateForms, currentState, dataSourceList, setContextState);
      setCustomFormButtonState(defaultCustomFormButtonStateValue);
      updateIsSummaryPageState(false, setContextState);
      formState.CustomForms = [];
    }

    if (isNewTicket) {
      dispatch({ action: "TemplateHasError", data: false });
      dispatch({ action: "TemplateErrorMessage", data: "" });
    } else {
      dispatch({ type: "TemplateHasError", data: false });
      dispatch({ type: "TemplateErrorMessage", data: "" });
    }

  }, [formState.SelectedTemplate]);

  useEffect(() => {
    switch (currentState.openSendFormDialogFrom) {
      case "sendForm":
      case "updateAnswer":
        setShowSendCurrentAnswerToggle(true);
        setTrueSendCurrentAnswer();
        break;
      case "applyTemplate":
      default:
        setShowSendCurrentAnswerToggle(false);
        setFalseSendCurrentAnswer();
        break;
    }

    setDisableSendCurrentAnswerToggle(currentState.openSendFormDialogFrom != "sendForm");
  }, [currentState.openSendFormDialogFrom])

  useEffect(() => {
    setDisableSendCurrentAnswerToggle(currentState.answerCustomFormNow);

    let selectedTemplate = formState.SelectedTemplate;

    if (!currentState.answerCustomFormNow) {
      setCustomFormButtonState(defaultCustomFormButtonStateValue);
    } else {
      if (!isNewTicket) {
        if (Object.keys(selectedTemplate).length <= 0)
          selectedTemplate = Array.isArray(formState.Template) ? formState.Template.filter(x => x.Id == formState.TemplateId)[0] : formState.Template;

        if (formState.CustomFormTemplate != null)
          selectedTemplate = formState.CustomFormTemplate;

        const customFormList = getDefaultCustomFormList(selectedTemplate?.TemplateForms, currentState, dataSourceList);

        const customFormWithAnswersList = customFormList.map(item1 => {
          const match = formState.CustomForms.find(item2 => item2.TemplateFormId === item1.TemplateFormId);
          if (match && canParseJson(match.CardAnswerJson)) {
            return { ...item1, CardAnswerJson: parseJsonRecovering(match.CardAnswerJson) };
          } else {
            return { ...item1 };
          }
        });
        updateCustomFormState(customFormWithAnswersList, setContextState);
      }

      setShowSummaryCard(selectedTemplate?.SummaryCardEnabled ?? false);
      updateIsSummaryPageState(false, setContextState);
      setCustomFormButtonState(preState => ({
        ...preState,
        showNextButton: true,
        showSubmitButton: false
      }));

    }
  }, [currentState.answerCustomFormNow])

  useEffect(() => {
    setContextState(prevState => ({
      ...prevState,
      customFormState: {
        ...prevState.customFormState,
        isSendCurrentAnswers: sendCurrentAnswer
      },
    }));
  }, [sendCurrentAnswer])

  useEffect(() => {
    setShowBackToTemplateButton(templatePage == 1 && currentState.openSendFormDialogFrom == "applyTemplate" && !currentState.answerCustomFormNow);
  }, [templatePage])

  useEffect(() => {
    setContinueButtonText(currentState.customFormState.activeForm?.SubmitButtonText);
  }, [currentState.customFormState.activeForm])

  useEffect(() => {
    if (currentState.customFormState.isSummaryPage && !showSummaryCard)
      templateNextAction();
  }, [currentState.customFormState.isSummaryPage])

  const getTemplatePageCountDialog = () => {
    if (formState.SelectedTemplate.NoCustomForm) {
      //this is when custom form is not used
      formState.SelectedTemplate.ClearFormAnswer = false;
      return 1;
    }
    else { //This will use the custom form
      return 2;
    }
  }

  const getTemplatePage = () => {
    if (templateListLoaded) {
      switch (templatePages[templatePage]) {
        case "select":
          return (
            <div className="mobile-tab-padding"><TicketTemplatesSelection
              formData={
                {
                  templates: templateList,
                  ...currentState,
                } as TemplateFormStateProps
              }
              ticketData={formState}
              dispatchEvent={dispatch}
            /></div>
          );
        case "view":
          return (
            <div className="mobile-tab-padding"><TicketTemplatesSelectionView
              formData={
                {
                  templates: templateList,
                  ...currentState,
                } as TemplateFormStateProps
              }
              ticketData={formState}
            /></div>
          );
        case "done":
          return (
            <div className="mobile-tab-padding"><TicketTemplatesSelectionComplete
              formData={
                {
                  templates: templateList,
                  ...currentState,
                } as TemplateFormStateProps
              }
              ticketData={formState}
              dispatchEvent={dispatch}
              dataSourceList={dataSourceList}
              affectedUserList={isNewTicket ? formState.Entity.AffectedUsers : formState.AffectedUsers}
              collaboratorList={isNewTicket ? formState.Entity.Collaborators : formState.TicketCollaborators}
            /></div>
          );
        default:
          return <></>;
      }
    } else
      return (
        <Flex column style={{ flex: 1, maxHeight: "40vh" }} gap="gap.large">
          <LoadingScreen message={GETTING_ALL_TEMPLATES} />
        </Flex>
      );
  };

  const templateBackToSelectionAction = () => {
    setTemplatePage(0);
    resetAnswerNow();
    resetButtonState();
  }

  const templateCancelAction = () => {
    setTemplatePage(0);
    closeApplyTemplate();
    resetAnswerNow();
    resetButtonState();
  }

  const templateNextAction = async () => {
    dispatch({ type: "IsTemplateSelectionSaving", data: true });
    const dispatchHasError = { type: "TemplateHasError", data: true };
    const dispatchErrorMessage = { type: "TemplateErrorMessage", data: t("ticket-details.apply-template.lifecycle-cannot-applied-resolved-closed") };

    if (formState.SelectedTemplate.Lifecycle) {
      const statusMatch = (isNewTicket ? formState.SelectedEnums?.StatusId : formState.Status)?.Guid ?? "";
      if ([STATUS_GUID.RESOLVED, STATUS_GUID.CLOSED].indexOf(statusMatch) >= 0) {
        if (!isNewTicket) {
          dispatch(dispatchHasError);
          dispatch(dispatchErrorMessage);
        } else {
          dispatch({ action: dispatchHasError.type, data: dispatchHasError.data });
          dispatch({ action: dispatchErrorMessage.type, data: dispatchErrorMessage.data });
        }
        return;
      }
      if (formState.Id && formState.Id > 0 && (ticketApprovals.length || currentState.hasActiveTasks)) {
        dispatch(dispatchHasError);
        dispatch({
          type: dispatchErrorMessage.type,
          data: t(
            "ticket-details.apply-template.lifecycle-cannot-applied-existing-approvals-tasks"
          ),
        });
        return;
      }
    } else if (isNewTicket) {
      dispatch({ action: dispatchHasError.type, data: false });
      dispatch({ action: dispatchErrorMessage.type, data: "" });
    }

    if (templatePage + 1 < getTemplatePageCountDialog()) {
      setTemplatePage(templatePage + 1);
    }
    else {
      if (currentState.openSendFormDialogFrom == "updateAnswer" || currentState.openSendFormDialogFrom == "sendForm") {
        if (currentState.answerCustomFormNow) {
          let customFormList = [];
          formState.SelectedTemplate.CustomForms.filter(x => x.IsAnswered).forEach(form => {
            formatInputRawData(form.CardTemplateJson.body, form.CardAnswerJson);
            customFormList.push({ TicketId: formState.Id, TemplateFormId: form.TemplateFormId, FormName: form.Name, Order: form.Order, CardTemplateJson: JSON.stringify(form.CardTemplateJson), CardAnswerJson: JSON.stringify(form.CardAnswerJson) });
          });
          templateCancelAction();

          updateIsCustomFormSavingState(true, setContextState);
          await api.updateCustomForms(formState.Id, customFormList);

          if (formState.UpdatedTicket.CardFileAttachments?.length > 0) {
            let updatedFormFiles: any[] = [];
            for (const formFile of formState.UpdatedTicket.CardFileAttachments) {
              const filesToAttach = await startFileUploadSession(api, uploadHelper, formFile.Files, formState.Id, false, "");
              updatedFormFiles.push({
                TemplateFormId: formFile.TemplateFormId,
                Files: filesToAttach,
                Message: formFile.Message,
                IsPrivate: formFile.IsPrivate
              });
            }

            await api.attachWebCustomFormFiles(formState.Id, customFormList, updatedFormFiles, false, true);
          }

          if (props.onInteraction)
            props.onInteraction({ event: EditTicketEvent.CustomFormUpdated, data: { hasUploaded: formState.UpdatedTicket.CardFileAttachments?.length > 0 } });

          toast.success(t('ticket-details.edit-ticket-form.custom-form-answer.success-response'), toastDefault);
        } else {
          templateCancelAction();
          await handleSendCustomForm(formState, currentState, setContextState);
          toast.success(t('ticket-details.edit-ticket-form.custom-form-answer.form-sent'), toastDefault);
          setContextState((prev) => ({
            ...prev,
            isSendFormLoading: false
          }));
        }
      } else {
        applyTemplate();
      }

      setTemplatePage(0);
      resetAnswerNow();
      resetButtonState();
    }
  };

  const templateFormPreviousAction = () => {
    handleBackCustomFormAction(currentState, setContextState, setCustomFormButtonState);
  };

  const templateFormNextAction = async () => {
    const selectedTemplateId = formState.SelectedTemplate?.Id ?? formState.TemplateId;
    const currentTemplate = currentState.customFormState.activeForm.CardTemplateJson;
    const currentAnswer = currentState.customFormState.activeForm.CardAnswerJson;
    const validInputs = validateRequiredInputs(currentTemplate, currentAnswer, currentState?.customProperties ?? []);

    if (validInputs) {
      await handleNextCustomFormAction(selectedTemplateId, currentState, setContextState, setCustomFormButtonState, formState.Id);
    }
  };

  const templateFormStartOverAction = () => {

    let selectedTemplate = formState.SelectedTemplate;

    if (Object.keys(selectedTemplate).length <= 0)
      selectedTemplate = Array.isArray(formState.Template) ? formState.Template.filter(x => x.Id == formState.TemplateId)[0] : formState.Template;

    if (selectedTemplate?.TemplateForms == undefined)
      selectedTemplate = formState.CustomFormTemplate;

    handleStartOVerCustomFormAction(selectedTemplate.TemplateForms, currentState, dataSourceList, setContextState, setCustomFormButtonState);
  };

  const resetAnswerNow = () => {
    setContextState(preState => ({
      ...preState,
      answerCustomFormNow: false,
      openSendFormDialogFrom: "applyTemplate"
    }));
  }

  const resetButtonState = () => {
    setCustomFormButtonState(defaultCustomFormButtonStateValue);
  }


  useEffect(() => {
    const isLastPage = templatePage + 1 >= getTemplatePageCountDialog();
    if (isLastPage && [SEND_FORM_OPTION.SEND_TO_AFFECTEDUSER, SEND_FORM_OPTION.SEND_TO_COLLABORATOR].includes(formState.SelectedTemplate.SendCustomFormSettings?.Type ?? "")) {
      if (formState.SelectedTemplate.SendCustomFormSettings.Type == SEND_FORM_OPTION.SEND_TO_AFFECTEDUSER)
        setDisableSaveSendBtn(!formState.SelectedTemplate.SendCustomFormSettings.SelectedAffectedUserId)
      if (formState.SelectedTemplate.SendCustomFormSettings.Type == SEND_FORM_OPTION.SEND_TO_COLLABORATOR)
        setDisableSaveSendBtn(!formState.SelectedTemplate.SendCustomFormSettings.SelectedCollaboratorId)
    } else
      setDisableSaveSendBtn(false);
  }, [formState.SelectedTemplate.SendCustomFormSettings])

  return (
    <ProviderConsumer
      render={globalTheme => (
        <Dialog styles={{
          [`@media screen and (max-width: 500px)`]: {
            width: 'fit-content',
          }
        }}
          className={"responsive-dialog top-0 fixed md:relative z-10 mobile-full"}
          content={(!isApplyingTemplateWithLifecycle)
            ? getTemplatePage()
            : <Flex
              column
              style={{ flex: 1, maxHeight: '40vh' }}
              gap="gap.large"
            >
              <LoadingScreen message={t('ticket-details.apply-template.dialog.applying-lifecycle-to-ticket')} />
            </Flex>
          }
          open={showTemplate}
          headerAction={(!isApplyingTemplateWithLifecycle) ? {
            icon: <CloseIcon />,
            title: t('ticket-details.apply-template.dialog.button.close'),
            onClick: templateCancelAction
          } : {}}
          footer={(!isApplyingTemplateWithLifecycle && templateListLoaded) ?
            <div
              className="flex flex-col gap-2 fixed left-0 bottom-0 p-3 w-screen md:static md:left-auto md:bottom-auto md:p-0 md:w-auto"
              style={{
                backgroundColor: getThemeMode() === "dark"
                  ? globalTheme.siteVariables.colorScheme.default.backgroundHover3
                  : globalTheme.siteVariables.colorScheme.default.background
              }}
            >
              <div className="flex gap-2 justify-center">
                {(showBackToTemplateButton && currentState.customFormState.isSummaryPage) && (
                  <div className="flex-1 md:hidden">
                    <Button fluid content={t('ticket-details.apply-template.dialog.button.back-to-templates')} onClick={templateBackToSelectionAction} />
                  </div>
                )}
              </div>
              <div className="flex flex-col md:flex-row gap-2 md:items-center">
                {showBackToTemplateButton && (
                  <div className={currentState.customFormState.isSummaryPage ? "hidden md:block" : ""}>
                    <Button text content={t("ticket-details.apply-template.dialog.button.back-to-templates")} onClick={templateBackToSelectionAction}></Button>
                  </div>
                )}
                {(showSendCurrentAnswerToggle) && (
                  <div className={`self-start md:self-auto ${(disableSendCurrentAnswerToggle) && MgtCss.disabledContainer}`}>
                    <Checkbox label={
                      <label style={{ whiteSpace: 'nowrap', marginLeft: '-5px', fontSize: 'smaller' }}>
                        {t('ticket-details.apply-template.dialog.send-current-answer')}
                      </label>} toggle onChange={toggleSendCurrentAnswer} checked={sendCurrentAnswer} />
                  </div>
                )}
                <div className="flex gap-2 md:ml-auto">
                  <div className="flex-1 md:flex-none">
                    <Button fluid content={t('ticket-details.apply-template.dialog.button.cancel')} onClick={templateCancelAction} />
                  </div>
                  {(customFormButtonState.showBackButton) && (
                    <div className="flex-1 md:flex-none">
                      <Button fluid content={t('ticket-details.apply-template.dialog.button.back')} onClick={templateFormPreviousAction} />
                    </div>
                  )}
                </div>
                {(customFormButtonState.showStartOverButton) && (
                  <div className="w-full md:w-auto">
                    <Button fluid content={t('ticket-details.apply-template.dialog.button.start-over')} onClick={templateFormStartOverAction} />
                  </div>
                )}
                {(customFormButtonState.showNextButton) && (
                  <div className="w-full md:w-auto">
                    <Button fluid primary content={continueButtonText} onClick={templateFormNextAction} />
                  </div>
                )}
                {(customFormButtonState.showSubmitButton) && (
                  <div className="w-full md:w-auto">
                    <Button
                      fluid
                      content={
                        templatePage + 1 < getTemplatePageCountDialog()
                          ? t('ticket-details.apply-template.dialog.button.next')
                          : formState &&
                            (!formState.SelectedTemplate.ClearFormAnswer || isNewTicket || currentState.customFormState.isSummaryPage)
                            ? t('ticket-details.apply-template.dialog.button.save')
                            : t('ticket-details.apply-template.dialog.button.save-send')
                      }
                      primary
                      onClick={templateNextAction}
                      disabled={(templatePages[templatePage] == 'select' && !formState?.SelectedTemplate.Id) || disableSaveSendBtn}
                    />
                  </div>
                )}
              </div>
            </div>
            : <></>}
        />
      )}
    />
  );

}